import { __decorate } from "tslib";
import { localized, msg, str } from '@lit/localize';
import debounce from 'debounce';
import { html, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { SlotController } from '../../shared/controllers/slot.controller.js';
import { submitForm } from '../../shared/forms/form-utils.js';
import { HelixTextFormControl } from '../../shared/forms/helix-text-form-control.js';
import { componentStyles } from '../../shared/styles/component.styles.js';
import { formControlStyles } from '../../shared/styles/form-control.styles.js';
import '../icon/icon.js';
import { textFieldStyles } from './text-field.styles.js';
/**
 * Text Field component to input a text value.
 *
 * @slot label – Label for the textarea, appears on top of it. @see HlxTextarea#label
 * @slot prefix – Custom section that appears on the inside left of the input. @see HlxTextarea#prefix
 * @slot suffix – Custom section that appears on the inside right of the input. @see HlxTextarea#suffix
 * @slot error-text – Custom error text that appears below the textarea. @see HlxTextarea#errorText
 * @slot help-text – Helper text that appears below the textarea in smaller font. @see HlxTextarea#helpText
 *
 * @event hlx-blur - Triggers when the control loses focus.
 * @event hlx-change - Triggers when the control value changes.
 * @event hlx-focus - Triggers when the control gains focus.
 * @event hlx-input - Triggers when the control receives input.
 * @event hlx-invalid - Triggers when the form control has been checked for validity and its constraints aren't satisfied.
 *
 * @csspart hlx-form-control - The form control container.
 * @csspart hlx-form-control-label - The label slot.
 * @csspart hlx-form-control-base - The base container.
 * @csspart hlx-form-control-prefix - The prefix slot.
 * @csspart hlx-form-control-input - The input element.
 * @csspart hlx-form-control-suffix - The suffix slot.
 * @csspart hlx-form-control-error - The error container.
 * @csspart hlx-form-control-help-text - The help container.
 */
let HlxTextField = class HlxTextField extends HelixTextFormControl {
    constructor() {
        super(...arguments);
        this.hasFocus = false;
        this.label = '';
        this.required = false;
        this.placeholder = '';
        this.prefixText = '';
        this.suffixText = '';
        this.errorText = '';
        this.helpText = '';
        this.disabled = false;
        this.readonly = false;
        this.size = 'medium';
        this.title = '';
        this.autocomplete = 'off';
        this.type = 'text';
        this.inputId = crypto.randomUUID();
        this.slotController = new SlotController(this, 'label', 'help-text');
        this.announce = debounce(() => {
            this.announcer.textContent = this.validation.errorText;
        }, 1000);
    }
    render() {
        const hasLabel = this.label || this.slotController.hasSlot('label');
        const hasHelpText = this.helpText || this.slotController.hasSlot('help-text');
        return html `
            <div
                part="hlx-form-control"
                class="${classMap({
            ...this.validation.classMap,
            'hlx-form-control-md': this.size === 'medium',
            'hlx-form-control-has-label': hasLabel,
            'hlx-form-control-has-help-text': hasHelpText,
        })}">
                <label
                    part="hlx-form-control-label"
                    class="${classMap({
            'hlx-form-control-label': true,
            'hlx-form-control-label-disabled': this.disabled,
        })}"
                    for=${this.inputId}>
                    <slot name="label">${this.label}</slot>
                </label>

                <div
                    part="hlx-form-control-base"
                    class="${classMap({
            'hlx-text-field': true,
            'hlx-text-field-md': this.size === 'medium',
            'hlx-text-field-disabled': this.disabled,
            'hlx-text-field-focused': this.hasFocus,
            'hlx-text-field-readonly': this.readonly,
        })}">
                    <slot
                        name="prefix"
                        part="hlx-form-control-prefix"
                        class="hlx-text-field-prefix">
                        ${this.prefixText ?
            html `
                                <span class="input-addon-default">${this.prefixText}</span>
                            `
            : nothing}
                    </slot>

                    <input
                        part="hlx-form-control-input"
                        id=${this.inputId}
                        type=${this.type}
                        class="${classMap({
            'hlx-text-field-control': true,
        })}"
                        name=${ifDefined(this.name)}
                        .value=${live(this.value)}
                        placeholder=${ifDefined(this.placeholder)}
                        title=${this.title}
                        autocomplete=${ifDefined(this.autocomplete)}
                        ?disabled=${this.disabled}
                        ?readonly=${this.readonly}
                        ?required=${this.required}
                        pattern=${ifDefined(this.pattern)}
                        minlength=${ifDefined(this.minlength)}
                        @change=${this.handleChange}
                        @blur=${this.handleBlur}
                        @focus=${this.handleFocus}
                        @input=${this.handleInput}
                        @keydown=${this.handleKeydown} />
                    <slot
                        name="suffix"
                        part="hlx-form-control-suffix"
                        class="hlx-text-field-suffix">
                        ${this.suffixText ?
            html `
                                <span class="input-addon-default">${this.suffixText}</span>
                            `
            : nothing}
                    </slot>
                </div>

                ${this.validation.renderErrorSlot()}

                <div
                    part="hlx-form-control-help-text"
                    class="${classMap({
            'hlx-form-control-help-text': true,
            'hlx-form-control-help-text-disabled': this.disabled,
        })}">
                    <slot name="help-text">${this.helpText}</slot>
                </div>
                <div class="hlx-text-field-announcer" aria-live="polite" role="log"></div>
            </div>
        `;
    }
    getDefaultErrorMessage(validity) {
        if (validity.valueMissing) {
            return msg('Please fill out this field.');
        }
        else if (validity.patternMismatch) {
            return msg('Please match the requested format.');
        }
        else if (validity.tooShort) {
            const need = getCharsStr(this.minlength);
            const current = getCharsStr(this.input.value.length);
            return msg(str `Please use at least ${need} (you are currently using ${current}).`);
        }
        else if (validity.tooLong) {
            const exceed = getCharsStr(this.input.value.length - this.maxlength);
            return msg(str `${exceed} too long.`);
        }
        else if (validity.typeMismatch) {
            if (this.type === 'url') {
                return msg('Please enter a URL.');
            }
            else if (this.type === 'email') {
                return msg('Please enter an email address.');
            }
        }
        return '';
    }
    handleKeydown(e) {
        if (e.key === 'Enter') {
            submitForm(this.getForm());
        }
    }
    handleChange() {
        this.value = this.input.value;
        this.emit('hlx-change');
    }
    handleBlur() {
        this.hasFocus = false;
        this.emit('hlx-blur');
    }
    handleFocus() {
        this.hasFocus = true;
        this.emit('hlx-focus');
    }
};
HlxTextField.styles = [componentStyles, formControlStyles, textFieldStyles];
__decorate([
    query('.hlx-text-field-announcer')
], HlxTextField.prototype, "announcer", void 0);
__decorate([
    state()
], HlxTextField.prototype, "hasFocus", void 0);
__decorate([
    property({ type: String })
], HlxTextField.prototype, "label", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxTextField.prototype, "required", void 0);
__decorate([
    property({ type: String, reflect: true })
], HlxTextField.prototype, "pattern", void 0);
__decorate([
    property({ type: String })
], HlxTextField.prototype, "placeholder", void 0);
__decorate([
    property({ type: String, attribute: 'prefix-text' })
], HlxTextField.prototype, "prefixText", void 0);
__decorate([
    property({ type: String, attribute: 'suffix-text' })
], HlxTextField.prototype, "suffixText", void 0);
__decorate([
    property({ type: String, attribute: 'error-text' })
], HlxTextField.prototype, "errorText", void 0);
__decorate([
    property({ type: String, attribute: 'help-text' })
], HlxTextField.prototype, "helpText", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxTextField.prototype, "disabled", void 0);
__decorate([
    property({ type: Boolean, reflect: true })
], HlxTextField.prototype, "readonly", void 0);
__decorate([
    property({ type: String, reflect: true })
], HlxTextField.prototype, "size", void 0);
__decorate([
    property({ type: String })
], HlxTextField.prototype, "title", void 0);
__decorate([
    property({ type: String })
], HlxTextField.prototype, "autocomplete", void 0);
__decorate([
    property({ type: String, reflect: true })
], HlxTextField.prototype, "type", void 0);
HlxTextField = __decorate([
    customElement('hlx-text-field'),
    localized()
], HlxTextField);
export { HlxTextField };
// This is not general case, but works for English & Spanish, which we support
// Unfortunately Lit-localize doesn't support pluralization ATM, see
// https://github.com/lit/lit/issues/1891
function getCharsStr(chars) {
    return msg(str `${chars} ${chars > 1 ? 'characters' : 'character'}`);
}
//# sourceMappingURL=text-field.js.map